import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row, Container, Form, Accordion, Button } from "react-bootstrap";
import _ from "lodash";

import { showAlert } from "../../../redux/slices/alertSlice";
import { arquiveCollaborator, getCollaborator, promoteCollaborator, updateCollaborator } from "../../../redux/slices/collaboratorSlice";
import CollaboratorSkillService from "../../../services/CollaboratorSkillService";
import SkillService from "../../../services/SkillService";


import "./CollaboratorEdit.css";
import duckNotFoundImg from "../../../assets/images/duckNotFound.svg";
import IconButton from "../../IconButton/IconButton";
import {
  AddSmallIcon,
  CancelIcon,
  ConfirmIcon,
  DeleteIcon,
  EditIcon,
  ChevronLeftIcon,
} from "../../Icon/icons";
import SkillsCard from "../../Skill/SkillsCard/SkillsCard";
import SkillExperience from "../../Skill/SkillExperience/SkillExperience";
import ConfirmationModal from "../../UI/ConfirmationModal/ConfirmationModal";
import ContainerSpinner from "../../UI/ContainerSpinner/ContainerSpinner";

const EDITING_MODES = {
  admin: "admin",
  adminSelf: "adminSelf",
  self: "self",
};

const SKILL_STATUS = {
  added: "added",
  updated: "updated",
  deleted: "deleted",
};

const MODAL_TYPE = {
  arquiveChange: "arquiveChange",
  editionSave: "editionSave",
};

const CollaboratorEdit = ({ setSubheaderUpdatedAt }) => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const collaboratorSlice = useSelector((state) => state.collaboratorSlice);
  const teamSlice = useSelector((state) => state.teamSlice);
  const senioritySlice = useSelector((state) => state.senioritySlice);
  const projectSlice = useSelector((state) => state.projectSlice);
  const skillCategorySlice = useSelector((state) => state.skillCategorySlice);
  const { user } = useSelector((state) => state.userSlice);

  const [collaborator, setCollaborator] = useState();
  const [newSkill, setNewSkill] = useState("");
  const [newSkillOptions, setNewSkillOptions] = useState({});
  const [groupedSkills, setGroupedSkills] = useState([]);

  const [accordionActiveKeys, setAccordionActiveKeys] = useState([]);
  const skillInputRef = useRef();

  const [showModalType, setShowModalType] = useState();
  const [editingMode, setEditingMode] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const goToHome = () => {
    navigate("/");
  };

  const handleShowAlert = (alert) => {
    dispatch(showAlert(alert));
  };

  const onChangeIsArquive = async () => {
    dispatch(arquiveCollaborator({ id: collaborator.user.id, is_archived: !collaborator.user.is_archived }));

    handleShowModalType(null);
    handleShowAlert({
      headingText: !collaborator.user.is_archived ? "Colaborador arquivado!" : "Colaborador desarquivado!",
      text: !collaborator.user.is_archived
        ? "Este colaborador foi arquivado com sucesso."
        : "Este colaborador foi desarquivado com sucesso.",
    });
  };

  const onChangeIsAdmin = () => {
    const newIsAdmin = !collaborator.user.is_admin;
    dispatch(promoteCollaborator({ id: collaborator.user.id, is_admin: newIsAdmin }));
    handleShowAlert({
      headingText: newIsAdmin ? "Usuário promovido!" : "Permissão removida!",
      text: newIsAdmin
        ? "Este colaborador agora é administrador."
        : "Este colaborador não é mais administrador.",
    });
  };

  const onChangeTeam = (e) => {
    const newTeam = teamSlice.teamList.find((s) => s.id === Number(e.target.value));
    setCollaborator({ ...collaborator, team: newTeam });
  };

  const onChangeSeniority = (e) => {
    const newSeniority = senioritySlice.seniorityList.find((s) => s.id === Number(e.target.value));
    setCollaborator({ ...collaborator, seniority: newSeniority });
  };

  const onChangeProject = (e) => {
    const newProject = projectSlice.projectList.find((p) => p.id === Number(e.target.value));
    setCollaborator({ ...collaborator, project: newProject });
  };

  const onChangeSkillExperience = (e, skillId) => {
    setCollaborator({
      ...collaborator,
      skills: collaborator.skills.map((skill) =>
        skill.id === skillId ? { ...skill, experience: e, status: skill.status || SKILL_STATUS.updated } : skill
      ),
    });
  };

  const onChangeNewSkill = (e) => {
    setNewSkill(e.target.value);
  };

  const handleOpenAddSkill = async (skillCategoryId) => {
    const skills = await SkillService.getByCategory({ skillCategoryId });
    const newSkillOptions = {
      skillCategoryId: skillCategoryId,
      skillOptions: skills,
    };

    setNewSkillOptions(newSkillOptions);
  };

  const handleCloseAddSkill = () => {
    setNewSkillOptions({});
  };

  const handleAddNewSkill = (skillCategory) => {
    const addedSkillName = newSkill.trim();

    const existingSkill = collaborator.skills.find((skill) => skill.skill.name === addedSkillName);
    if (existingSkill) {
      // Se for uma skill deletada, será reinserida sem mostrar o alerta
      if (existingSkill.status === SKILL_STATUS.deleted) {
        setCollaborator({
          ...collaborator,
          skills: collaborator.skills.map((skill) =>
            skill.id === existingSkill.id ? { ...skill, experience: 1, status: SKILL_STATUS.updated } : skill
          ),
        });
      } else {
        handleShowAlert({
          headingText: "Habilidade já cadastrada!",
          text: "A habilidade informada já existe no cadastro.",
        });
      }
    } else {
      const existingSkillsInOptions = newSkillOptions.skillOptions.filter((skill) => skill.name === addedSkillName);

      // Se a skill adicionada não existir no banco de dados, a mesma será criada ao salvar
      const addedSkill = existingSkillsInOptions.length
        ? existingSkillsInOptions[0]
        : {
            id: null,
            name: addedSkillName,
            category: skillCategory,
          };

      const addedCollaboratorSkill = {
        collaborator: collaborator.id,
        skill: addedSkill,
        experience: 1,
      };

      // Este id é adicionado apenas para objeto funcionar corretamente
      addedCollaboratorSkill.id = Math.max(...collaborator.skills.map((skill) => skill.id), 0) + 1;
      addedCollaboratorSkill.status = SKILL_STATUS.added;

      setCollaborator({
        ...collaborator,
        skills: [...collaborator.skills, addedCollaboratorSkill],
      });

      setNewSkill("");
    }
  };

  const handleDeleteSkill = (skillId) => {
    setCollaborator({
      ...collaborator,
      skills: collaborator.skills.map((skill) =>
        skill.id === skillId ? { ...skill, experience: 0, status: skill.status || SKILL_STATUS.updated } : skill
      ),
    });
  };

  const handleOpenEdition = () => {
    setIsEditing(true);
  };

  const handleTrySaveEdition = () => {
    if (editingMode !== EDITING_MODES.self && !collaborator.project) {
      handleShowAlert({
        headingText: "Projeto é obrigatório!",
        text: "É obrigatório selecionar um projeto.",
      });
    } else {
      handleShowModalType(MODAL_TYPE.editionSave);
    }
  };

  const handleSaveEdition = async () => {
    setLoading(true)
    let skills = Object.values(collaborator.skills);
    if (editingMode !== EDITING_MODES.admin) {
      if (skills.filter(skills => skills.status === SKILL_STATUS.updated).length > 0) {
        let updated = skills.filter(skills => skills.status === SKILL_STATUS.updated);
        await CollaboratorSkillService.bulkUpdate({
          data: updated
        });
      }else{
        for (const skill of skills) {
          if (!skill.status) {
            continue;
          }

          if (skill.status === SKILL_STATUS.added) {
            let addedSkillId = skill.skill.id;
            if (!addedSkillId) {
              // A skill será criada no banco de dados se não existir ainda
              const data = await SkillService.insert({
                name: skill.skill.name,
                categoryId: skill.skill.category.id,
              });

              addedSkillId = data.id;
            }

            await CollaboratorSkillService.insert({
              collaboratorId: collaborator.id,
              skillId: addedSkillId,
              experience: skill.experience,
            });
          } else if (skill.status === SKILL_STATUS.updated) {
            await CollaboratorSkillService.update({
              id: skill.id,
              experience: skill.experience,
            });
          } else if (skill.status === SKILL_STATUS.deleted) {
            await CollaboratorSkillService.delete({
              id: skill.id,
            });
          }
        }
      }
    }

    if (editingMode !== EDITING_MODES.self) {
      dispatch(
        updateCollaborator({
          id: collaborator.id,
          teamId: collaborator.team.id,
          seniorityId: collaborator.seniority.id,
          projectId: collaborator.project.id,
        })
      );
    } else {
      dispatch(getCollaborator({ id: id || user.collaborator_id }));
    }
    setLoading(false)
    setIsEditing(false);
    handleShowModalType(null);
    handleShowAlert({
      headingText: "Salvo com sucesso!",
      text: "As alterações foram salvas com sucesso.",
    });
  };

  const handleCancelEdition = () => {
    setCollaborator(collaboratorSlice.collaboratorDetail);
    setNewSkill("");
    setNewSkillOptions({});

    setIsEditing(false);
  };

  const handleShowModalType = (modalType) => {
    setShowModalType(modalType);
  };

  const handleCollapsedAccordion = (newState) => {
    setAccordionActiveKeys((state) => {
      return [...state, newState];
    });
  };

  const handleAccordionClick = (itemPosition) => {
    if (accordionActiveKeys.includes(itemPosition)) {
      const filteredAccordionActiveKeys = accordionActiveKeys.filter((collapsed) => collapsed !== itemPosition);

      setAccordionActiveKeys(filteredAccordionActiveKeys);
      return;
    }

    handleCollapsedAccordion(itemPosition);
  };

  const openCategoryAccordion = (itemPosition) => {
    if (accordionActiveKeys.includes(itemPosition)) {
      return;
    }

    handleCollapsedAccordion(itemPosition);
  };

  const getModalsView = () => {
    return (
      <>
        <ConfirmationModal
          show={showModalType === MODAL_TYPE.arquiveChange}
          onHide={() => handleShowModalType(null)}
          buttonPrimaryText={collaborator.user.is_archived ? "Desarquivar" : "Arquivar"}
          buttonPrimaryOnClick={onChangeIsArquive}
          buttonSecondaryText="Cancelar"
          buttonSecondaryOnClick={() => handleShowModalType(null)}
        >
          {collaborator.user.is_archived ? (
            <>
              <h4>Desarquivar Colaborador</h4>
              <p>Deseja realmente desarquivar este colaborador?</p>
            </>
          ) : (
            <>
              <h4>Arquivar Colaborador</h4>
              <p>Deseja realmente arquivar este colaborador?</p>
            </>
          )}
        </ConfirmationModal>

        <ConfirmationModal
          show={showModalType === MODAL_TYPE.editionSave}
          onHide={() => handleShowModalType(null)}
          buttonPrimaryText="Salvar"
          buttonPrimaryOnClick={handleSaveEdition}
          buttonSecondaryText="Cancelar"
          buttonSecondaryOnClick={() => handleShowModalType(null)}
          loading={loading}
        >
          <h4>Salvar Alterações</h4>
          <p>Deseja salvar as alterações feitas no colaborador?</p>
        </ConfirmationModal>
      </>
    );
  };

  const getAccordionView = () => {
    const filteredSkillCategories = skillCategorySlice.skillCategoryList.filter((category) => {
      const isCategoryNotEmpty = groupedSkills[category.name];
      return isCategoryNotEmpty || editingMode !== EDITING_MODES.admin;
    });

    return (
      <Accordion defaultActiveKey={Object.keys(skillCategorySlice.skillCategoryList).map(Number)} alwaysOpen>
        {filteredSkillCategories.length ? (
          filteredSkillCategories.map((skillCategory, index) => (
            <Accordion.Item key={skillCategory.id} eventKey={index}>
              <div className="accordion-header-container">
                <Accordion.Header onClick={() => handleAccordionClick(index)}>{skillCategory.name}</Accordion.Header>
                {isEditing && editingMode !== EDITING_MODES.admin && getAccordionActionsView(skillCategory, index)}
              </div>
              {getAccordionSkillsView(skillCategory)}
            </Accordion.Item>
          ))
        ) : (
          <div className="accordion-no-skill-container">
            <img src={duckNotFoundImg} alt="Ilustração" />
            <p>Ops! Esse colaborador não possui habilidades cadastradas.</p>
          </div>
        )}
      </Accordion>
    );
  };

  const getAccordionActionsView = (skillCategory, categoryIndex) => {
    if (newSkillOptions.skillCategoryId === skillCategory.id) {
      return (
        <div ref={skillInputRef}>
          <input className="skill-input" list="skillInputOptions" value={newSkill} onChange={onChangeNewSkill} />
          <datalist id="skillInputOptions">
            {newSkillOptions.skillOptions.map((skillOption, key) => (
              <option key={key} value={skillOption.name} />
            ))}
          </datalist>

          <IconButton
            className="skill-input-add"
            disabled={newSkill.trim() === ""}
            onClick={() => {
              handleAddNewSkill(skillCategory);
              openCategoryAccordion(categoryIndex);
            }}
          >
            <AddSmallIcon />
          </IconButton>
        </div>
      );
    } else {
      return (
        <IconButton
          style={{ paddingRight: 0 }}
          onClick={() => {
            handleOpenAddSkill(skillCategory.id);
          }}
        >
          {/* Add new skill button disabled AddIcon */}
        </IconButton>
      );
    }
  };

  const showEmptySkills = () => {
    return isEditing? 0 : 1
  }

  const getAccordionSkillsView = (skillCategory) => {
    return groupedSkills[skillCategory.name]?.map((skill) => (
      skill.experience >= showEmptySkills() && (
      <Accordion.Body key={skill.id}>
        {skill.skill.name}
        <span className="float-end">
          <SkillExperience
            stars={skill.experience}
            readonly={!isEditing || editingMode === EDITING_MODES.admin}
            onChange={(e) => onChangeSkillExperience(e, skill.id)}
          />
          {skill.experience >0 && isEditing && editingMode !== EDITING_MODES.admin && (
            <IconButton style={{ paddingRight: 0 }} onClick={() => handleDeleteSkill(skill.id)}>
              <DeleteIcon />
            </IconButton>
          )}
        </span>
      </Accordion.Body>
    )));
  };

  const handleIsArquiveClassName = (className) => {
    if (className) {
      return collaborator.user.is_archived ? "collaborator-archived " + className : className;
    }

    if (collaborator.user.is_archived) {
      return "collaborator-archived";
    }
  };

  useEffect(() => {
    if (user.is_admin) {
      if (Number(id) === user.collaborator_id) {
        setEditingMode(EDITING_MODES.adminSelf);
      } else {
        setEditingMode(EDITING_MODES.admin);
      }
    } else {
      setEditingMode(EDITING_MODES.self);
    }

    dispatch(getCollaborator({ id: id || user.collaborator_id }));
  }, [dispatch, id, user]);

  useEffect(() => {
    if (collaboratorSlice.collaboratorDetail) {
      setCollaborator(collaboratorSlice.collaboratorDetail);
      setSubheaderUpdatedAt(collaboratorSlice.collaboratorDetail.updated_at);
    }
  }, [collaboratorSlice.collaboratorDetail, setSubheaderUpdatedAt]);

  useEffect(() => {
    if (collaborator?.skills) {
      setGroupedSkills(
        _.groupBy(
          collaborator.skills.filter((skill) => skill.status !== SKILL_STATUS.deleted),
          "skill.category.name"
        )
      );
    }
  }, [collaborator, isEditing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skillInputRef.current && !skillInputRef.current.contains(event.target)) {
        handleCloseAddSkill();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [skillInputRef]);

  if (collaboratorSlice.loading || !collaborator) {
    return <ContainerSpinner />;
  }

  return (
    <Container>
      {getModalsView()}

      <Row>
        <Col lg={8} className="collaborator-actions">
          {editingMode !== EDITING_MODES.self && (
            <IconButton onClick={goToHome} className="icon-button-return">
              <ChevronLeftIcon />
            </IconButton>
          )}

          <p className={handleIsArquiveClassName("collaborator-name")}>{collaborator.user.name}</p>

          {editingMode !== EDITING_MODES.self &&
            (isEditing ? (
              <>
                <Form.Select
                  defaultValue={collaborator.team.id}
                  onChange={onChangeTeam}
                  className={"collaborator-team"}
                >
                  {teamSlice.teamList.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>

                <Form.Select
                  defaultValue={collaborator.seniority.id}
                  onChange={onChangeSeniority}
                  className={`collaborator-seniority seniority-badge-${collaborator.seniority.id}`}
                >
                  {senioritySlice.seniorityList.map((seniority) => (
                    <option key={seniority.id} value={seniority.id}>
                      {seniority.name}
                    </option>
                  ))}
                </Form.Select>
              </>
            ) : (
              <>
                <p className={handleIsArquiveClassName("collaborator-team")}>{collaborator.team.name}</p>
                <p
                  className={handleIsArquiveClassName(
                    `collaborator-seniority seniority-badge-${collaborator.seniority.id}`
                  )}
                >
                  {collaborator.seniority.name}
                </p>
              </>
            ))}

          {!collaborator.user.is_archived && (
            <div className="ms-auto">
              {isEditing ? (
                <div>
                  <IconButton title="Cancelar Edição" onClick={handleCancelEdition}>
                    <CancelIcon />
                  </IconButton>
                  <IconButton
                    title="Salvar Edição"
                    style={{ paddingRight: 0 }}
                    disabled={_.isEqual(collaborator, collaboratorSlice.collaboratorDetail)}
                    onClick={handleTrySaveEdition}
                  >
                    <ConfirmIcon />
                  </IconButton>
                </div>
              ) : (
                <IconButton title="Editar" style={{ paddingRight: 0 }} onClick={handleOpenEdition}>
                  <EditIcon />
                </IconButton>
              )}
            </div>
          )}
        </Col>
        <Col>
          {editingMode === EDITING_MODES.admin && (
            <div className="d-flex align-items-center justify-content-end gap-3">
              <Form.Check
                type="switch"
                label="Administrador"
                checked={collaborator.user.is_admin}
                disabled={isEditing}
                onChange={onChangeIsAdmin}
              />
              {collaborator.user.is_archived ? (
                <Button
                  variant="primary"
                  disabled={isEditing}
                  onClick={() => handleShowModalType(MODAL_TYPE.arquiveChange)}
                >
                  Desarquivar Colaborador
                </Button>
              ) : (
                <Button
                  variant="outline-primary"
                  disabled={isEditing}
                  onClick={() => handleShowModalType(MODAL_TYPE.arquiveChange)}
                >
                  Arquivar Colaborador
                </Button>
              )}
            </div>
          )}
        </Col>
      </Row>

      <Row className={handleIsArquiveClassName("py-1")}>
        {isEditing && editingMode !== EDITING_MODES.self ? (
          <Form.Select
            defaultValue={collaborator.project?.id}
            onChange={onChangeProject}
            className="collaborator-project"
          >
            <option key={null} value={""}>
              Selecione o projeto
            </option>
            {projectSlice.projectList?.map((project) => (
              <option value={project.id} key={project.id}>
                {project.name}
              </option>
            ))}
          </Form.Select>
        ) : (
          <p className="collaborator-project">@{collaborator.project?.name ?? "Sem projeto"}</p>
        )}
      </Row>

      <Row className="py-1">
        <Col lg={8} className={handleIsArquiveClassName()}>
          {getAccordionView()}
        </Col>
        <Col lg={4}>
          <SkillsCard {...collaborator}/>
        </Col>
      </Row>
    </Container>
  );
};

export default CollaboratorEdit;
